import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  UpdateDisplaySettingsLanguagesMutationRequest,
  UpdateDisplaySettingsLanguagesMutationResponse,
} from "../types/UpdateDisplaySettingsLanguages"

type UpdateDisplaySettingsLanguagesClient = typeof client<
  UpdateDisplaySettingsLanguagesMutationResponse,
  Error,
  UpdateDisplaySettingsLanguagesMutationRequest
>
type UpdateDisplaySettingsLanguages = {
  data: UpdateDisplaySettingsLanguagesMutationResponse
  error: Error
  request: UpdateDisplaySettingsLanguagesMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<UpdateDisplaySettingsLanguagesClient>>
  client: {
    parameters: Partial<Parameters<UpdateDisplaySettingsLanguagesClient>[0]>
    return: Awaited<ReturnType<UpdateDisplaySettingsLanguagesClient>>
  }
}
/**
 * @summary Update display settings languages
 * @link /v1/display-settings/update-display-settings-languages
 */
export function useUpdateDisplaySettingsLanguages(
  options: {
    mutation?: UseMutationOptions<
      UpdateDisplaySettingsLanguages["response"],
      UpdateDisplaySettingsLanguages["error"],
      UpdateDisplaySettingsLanguages["request"]
    >
    client?: UpdateDisplaySettingsLanguages["client"]["parameters"]
  } = {}
): UseMutationResult<
  UpdateDisplaySettingsLanguages["response"],
  UpdateDisplaySettingsLanguages["error"],
  UpdateDisplaySettingsLanguages["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    UpdateDisplaySettingsLanguages["response"],
    UpdateDisplaySettingsLanguages["error"],
    UpdateDisplaySettingsLanguages["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        UpdateDisplaySettingsLanguages["data"],
        UpdateDisplaySettingsLanguages["error"],
        UpdateDisplaySettingsLanguages["request"]
      >({
        method: "post",
        url: `/v1/display-settings/update-display-settings-languages`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
